import LoaderButton from 'components/LoaderButton'
import { useState, useEffect, useRef } from 'react'
import { AsyncTypeahead, Typeahead } from 'react-bootstrap-typeahead'
import Alert from 'react-bootstrap/Alert'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import {
  TIMEZONES,
  buildSeed,
  DEFAULT_SETTINGS,
  isEmpty,
  SUGGESTED_FEEDS,
  setIn,
  TICKER_MAX_MESSAGES,
  TICKER_MESSAGE_MAX_LENGTH,
} from './screenWidgetSettings'
import WidgetFontPicker from './WidgetFontPicker'

function getError(errors, field) {
  if (!errors[field]) {
    return ''
  }
  return Array.isArray(errors[field]) ? errors[field][0] : errors[field]
}

const HEX_COLOUR = /^#[0-9A-Fa-f]{6}$/
const PICKER_FALLBACK = '#12151e'

// On blur: adds a leading '#' if the user left it off.
function withHash(value) {
  if (value === '' || value.startsWith('#')) return value
  return `#${value}`
}

const FEED_FIELDS = [
  { group: 'news_card', label: 'News card' },
  { group: 'ticker', label: 'Ticker' },
]

function ScreenWidgetSettingsSection({
  initialSettings,
  onSave,
  errors = {},
  errorMessage = '',
  isSaving = false,
}) {
  const seed = buildSeed(initialSettings)
  const [settings, setSettings] = useState(seed)
  const timezoneRef = useRef()
  const locationRef = useRef()
  const [hasChanges, setHasChanges] = useState(false)
  const [locationOptions, setLocationOptions] = useState([])
  const [isLoadingLocations, setIsLoadingLocations] = useState(false)
  // Per-message errors are keyed by index, so they're wrong once a message is removed.
  const [messageErrorsStale, setMessageErrorsStale] = useState(false)

  useEffect(() => {
    setMessageErrorsStale(false)
  }, [errors])

  useEffect(() => {
    setSettings(buildSeed(initialSettings))
    setHasChanges(false)
  }, [initialSettings])

  const change = (path, value) => {
    setSettings(prev => setIn(prev, path, value))
    setHasChanges(true)
  }

  const handleToggleEnable = checked => {
    setSettings(prev => {
      if (!checked) return { ...prev, enabled: false }
      let next = { ...prev, enabled: true }
      const defaults = DEFAULT_SETTINGS.settings
      for (const group of Object.keys(defaults)) {
        for (const key of Object.keys(defaults[group])) {
          const current = prev.settings?.[group]?.[key]
          if (isEmpty(current)) {
            next = setIn(next, ['settings', group, key], defaults[group][key])
          }
        }
      }
      return next
    })
    setHasChanges(true)
  }

  const formatLocation = option =>
    [option.name, option.admin1, option.country].filter(Boolean).join(', ')

  const handleLocationSearch = async query => {
    setIsLoadingLocations(true)
    try {
      const url = new URL(
        process.env.REACT_APP_GEOCODING_URL ||
          'https://geocoding-api.open-meteo.com/v1/search'
      )
      url.searchParams.set('name', query)
      url.searchParams.set('count', '10')
      url.searchParams.set('language', 'en')
      url.searchParams.set('format', 'json')
      if (process.env.REACT_APP_GEOCODING_API_KEY) {
        url.searchParams.set('apikey', process.env.REACT_APP_GEOCODING_API_KEY)
      }
      const res = await fetch(url.toString())
      const data = await res.json()
      setLocationOptions(data.results || [])
    } catch {
      setLocationOptions([])
    } finally {
      setIsLoadingLocations(false)
    }
  }

  const handlePickSuggestion = (group, suggestion) => {
    setSettings(prev => {
      let next = setIn(prev, ['settings', group, 'feed_url'], suggestion.url)
      if (group === 'news_card') {
        next = setIn(next, ['settings', group, 'source_label'], suggestion.label)
      }
      return next
    })
    setHasChanges(true)
  }

  const handleCancel = () => {
    setSettings(buildSeed(initialSettings))
    setHasChanges(false)
  }

  const handleSave = () => {
    onSave(settings)
  }

  const disabled = !settings.enabled
  const tickerSource = settings.settings.ticker.source
  // Always show at least one input so there's somewhere to type.
  const tickerMessages = settings.settings.ticker.messages.length
    ? settings.settings.ticker.messages
    : ['']

  const tickerMessagesError = getError(errors, 'settings.ticker.messages')

  const changeTickerMessages = messages =>
    change(['settings', 'ticker', 'messages'], messages)

  return (
    <div>
      <p>
        Configure the widget frame that displays around playlist content on this screen.
        When enabled, the frame appears as a clock header, news card, weather panel, and
        ticker around every slide (unless a slide has &ldquo;Hide widgets&rdquo; enabled).
      </p>

      <Card className='mb-3'>
        <Card.Body>
          <Form.Check
            type='switch'
            id='widgets-enabled'
            label='Enable widgets'
            checked={settings.enabled}
            onChange={e => handleToggleEnable(e.target.checked)}
          />
        </Card.Body>
      </Card>

      <Card className='mb-3'>
        <Card.Header>Branding</Card.Header>
        <Card.Body>
          <p className='text-muted'>
            Choose a custom font and colour for the widget frame (the clock, weather, news
            and ticker around your content).
          </p>
          <Form.Group>
            <Form.Label>Font</Form.Label>
            <WidgetFontPicker
              disabled={disabled}
              value={settings.settings.branding.font}
              isInvalid={Boolean(getError(errors, 'settings.branding.font'))}
              onChange={font => change(['settings', 'branding', 'font'], font)}
            />
            {getError(errors, 'settings.branding.font') && (
              <Form.Text className='text-danger'>
                {getError(errors, 'settings.branding.font')}
              </Form.Text>
            )}
            <p className='text-muted mb-1 mt-1'>Used for all text in the widget frame.</p>
          </Form.Group>

          <Form.Group className='mb-0'>
            <Form.Label htmlFor='branding-colour'>Brand colour</Form.Label>
            <div className='d-flex align-items-center' style={{ gap: '8px' }}>
              <input
                type='color'
                aria-label='Pick brand colour'
                disabled={disabled}
                value={
                  HEX_COLOUR.test(settings.settings.branding.colour || '')
                    ? settings.settings.branding.colour.toLowerCase()
                    : PICKER_FALLBACK
                }
                style={{ width: '48px', height: '38px', padding: 0, border: 0 }}
                onChange={e =>
                  change(['settings', 'branding', 'colour'], e.target.value.toUpperCase())
                }
              />
              <Form.Control
                id='branding-colour'
                disabled={disabled}
                value={settings.settings.branding.colour || ''}
                placeholder='Default'
                style={{ fontFamily: 'monospace', maxWidth: '140px' }}
                isInvalid={Boolean(getError(errors, 'settings.branding.colour'))}
                onChange={e =>
                  change(
                    ['settings', 'branding', 'colour'],
                    e.target.value.trim().toUpperCase()
                  )
                }
                onBlur={e => {
                  const value = withHash(e.target.value)
                  if (value !== e.target.value) {
                    change(['settings', 'branding', 'colour'], value)
                  }
                }}
              />
              <button
                type='button'
                className='btn btn-link'
                disabled={disabled || !settings.settings.branding.colour}
                onClick={() => change(['settings', 'branding', 'colour'], '')}>
                Reset
              </button>
            </div>
            {getError(errors, 'settings.branding.colour') && (
              <Form.Text className='text-danger'>
                {getError(errors, 'settings.branding.colour')}
              </Form.Text>
            )}
            <p className='text-muted mb-1 mt-1'>
              The whole widget frame uses this colour. Text colours adjust automatically
              so they stay readable. Leave blank for the default dark frame.
            </p>
          </Form.Group>
        </Card.Body>
      </Card>

      <Card className='mb-3'>
        <Card.Header>Clock</Card.Header>
        <Card.Body>
          <Form.Group>
            <Form.Label>Timezone</Form.Label>
            <Typeahead
              ref={timezoneRef}
              id='clock-timezone'
              disabled={disabled}
              options={TIMEZONES}
              labelKey='label'
              selected={
                settings.settings.clock.timezone
                  ? TIMEZONES.filter(tz => tz.id === settings.settings.clock.timezone)
                  : []
              }
              onFocus={() => timezoneRef.current?.getInput()?.select()}
              onChange={selected =>
                change(
                  ['settings', 'clock', 'timezone'],
                  selected[0] ? selected[0].id : ''
                )
              }
              inputProps={{
                isInvalid: Boolean(getError(errors, 'settings.clock.timezone')),
              }}
            />
            {getError(errors, 'settings.clock.timezone') && (
              <Form.Text className='text-danger'>
                {getError(errors, 'settings.clock.timezone')}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className='mb-0'>
            <Form.Label>Time Format</Form.Label>
            <Form.Control
              as='select'
              disabled={disabled}
              value={settings.settings.clock.format || ''}
              isInvalid={Boolean(getError(errors, 'settings.clock.format'))}
              onChange={e => change(['settings', 'clock', 'format'], e.target.value)}>
              <option value='12h'>12 Hour</option>
              <option value='24h'>24 Hour</option>
            </Form.Control>
            {getError(errors, 'settings.clock.format') && (
              <Form.Text className='text-danger'>
                {getError(errors, 'settings.clock.format')}
              </Form.Text>
            )}
          </Form.Group>
        </Card.Body>
      </Card>

      <Card className='mb-3'>
        <Card.Header>Weather</Card.Header>
        <Card.Body>
          <Form.Group>
            <Form.Label>Location</Form.Label>
            <AsyncTypeahead
              ref={locationRef}
              id='weather-location'
              disabled={disabled}
              isLoading={isLoadingLocations}
              options={locationOptions}
              labelKey={formatLocation}
              minLength={2}
              onFocus={() => locationRef.current?.getInput()?.select()}
              onSearch={handleLocationSearch}
              selected={
                settings.settings.weather.latitude != null
                  ? [
                      {
                        name: settings.settings.weather.location_name,
                        latitude: settings.settings.weather.latitude,
                        longitude: settings.settings.weather.longitude,
                      },
                    ]
                  : []
              }
              onChange={selected => {
                if (selected[0]) {
                  setSettings(prev => {
                    let next = setIn(
                      prev,
                      ['settings', 'weather', 'location_name'],
                      formatLocation(selected[0])
                    )
                    next = setIn(
                      next,
                      ['settings', 'weather', 'latitude'],
                      selected[0].latitude
                    )
                    next = setIn(
                      next,
                      ['settings', 'weather', 'longitude'],
                      selected[0].longitude
                    )
                    return next
                  })
                  setHasChanges(true)
                } else {
                  setSettings(prev => {
                    let next = setIn(prev, ['settings', 'weather', 'location_name'], '')
                    next = setIn(next, ['settings', 'weather', 'latitude'], null)
                    next = setIn(next, ['settings', 'weather', 'longitude'], null)
                    return next
                  })
                  setHasChanges(true)
                }
              }}
              placeholder='Search for a city...'
              inputProps={{
                isInvalid: Boolean(getError(errors, 'settings.weather.location_name')),
              }}
            />
            {getError(errors, 'settings.weather.location_name') && (
              <Form.Text className='text-danger'>
                {getError(errors, 'settings.weather.location_name')}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className='mb-0'>
            <Form.Label>Units</Form.Label>
            <Form.Control
              as='select'
              disabled={disabled}
              value={settings.settings.weather.units || ''}
              isInvalid={Boolean(getError(errors, 'settings.weather.units'))}
              onChange={e => change(['settings', 'weather', 'units'], e.target.value)}>
              <option value='metric'>Metric</option>
              <option value='imperial'>Imperial</option>
            </Form.Control>
            {getError(errors, 'settings.weather.units') && (
              <Form.Text className='text-danger'>
                {getError(errors, 'settings.weather.units')}
              </Form.Text>
            )}
          </Form.Group>
        </Card.Body>
      </Card>

      {FEED_FIELDS.map(field => (
        <Card className='mb-3' key={field.group}>
          <Card.Header>{field.label}</Card.Header>
          <Card.Body>
            {field.group === 'news_card' && (
              <Form.Group>
                <Form.Check
                  type='switch'
                  id='news-card-show-images'
                  disabled={disabled}
                  label='Show images from feed'
                  checked={settings.settings.news_card.show_images}
                  onChange={e =>
                    change(['settings', 'news_card', 'show_images'], e.target.checked)
                  }
                />
                <p className='text-muted mb-1 mt-1'>
                  Some feeds publish low-quality images. Turn off to show headline only.
                </p>
              </Form.Group>
            )}
            {field.group === 'news_card' && (
              <Form.Group>
                <Form.Label>Source label</Form.Label>
                <Form.Control
                  disabled={disabled}
                  value={settings.settings.news_card.source_label || ''}
                  maxLength={40}
                  placeholder='e.g. BBC News'
                  isInvalid={Boolean(getError(errors, 'settings.news_card.source_label'))}
                  onChange={e =>
                    change(['settings', 'news_card', 'source_label'], e.target.value)
                  }
                />
                {getError(errors, 'settings.news_card.source_label') && (
                  <Form.Text className='text-danger'>
                    {getError(errors, 'settings.news_card.source_label')}
                  </Form.Text>
                )}
                <p className='text-muted mb-1 mt-1'>
                  Max 40 characters. Shown above the headline.
                </p>
              </Form.Group>
            )}
            {field.group === 'ticker' && (
              <Form.Group>
                <Form.Label>Source</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type='radio'
                    id='ticker-source-rss'
                    name='ticker-source'
                    label='RSS feed'
                    disabled={disabled}
                    checked={tickerSource !== 'text'}
                    onChange={() => change(['settings', 'ticker', 'source'], 'rss')}
                  />
                  <Form.Check
                    inline
                    type='radio'
                    id='ticker-source-text'
                    name='ticker-source'
                    label='Custom text'
                    disabled={disabled}
                    checked={tickerSource === 'text'}
                    onChange={() => change(['settings', 'ticker', 'source'], 'text')}
                  />
                </div>
              </Form.Group>
            )}
            {field.group === 'ticker' && tickerSource === 'text' && (
              <Form.Group className='mb-0'>
                <Form.Label>Messages</Form.Label>
                {tickerMessages.map((message, i) => {
                  const error = messageErrorsStale
                    ? ''
                    : getError(errors, `settings.ticker.messages.${i}`)
                  return (
                    <div key={i} className='mb-2'>
                      <div className='d-flex'>
                        <Form.Control
                          disabled={disabled}
                          value={message || ''}
                          maxLength={TICKER_MESSAGE_MAX_LENGTH}
                          placeholder='e.g. 20% off all coffee before 10am'
                          isInvalid={
                            Boolean(error) || (Boolean(tickerMessagesError) && !message)
                          }
                          onChange={e =>
                            changeTickerMessages(
                              tickerMessages.map((m, j) => (j === i ? e.target.value : m))
                            )
                          }
                        />
                        {tickerMessages.length > 1 && (
                          <button
                            type='button'
                            disabled={disabled}
                            className='btn btn-outline-danger ml-2'
                            aria-label='Remove message'
                            onClick={() => {
                              changeTickerMessages(
                                tickerMessages.filter((_, j) => j !== i)
                              )
                              setMessageErrorsStale(true)
                            }}>
                            &times;
                          </button>
                        )}
                      </div>
                      {error && <Form.Text className='text-danger'>{error}</Form.Text>}
                    </div>
                  )
                })}
                {tickerMessagesError && (
                  <Form.Text className='text-danger mb-2'>
                    {tickerMessagesError}
                  </Form.Text>
                )}
                {tickerMessages.length < TICKER_MAX_MESSAGES && (
                  <button
                    type='button'
                    disabled={disabled}
                    className='btn btn-link p-0 shadow-none'
                    onClick={() => changeTickerMessages([...tickerMessages, ''])}>
                    + Add message
                  </button>
                )}
                <p className='text-muted mb-1 mt-1'>
                  Up to {TICKER_MAX_MESSAGES} messages, {TICKER_MESSAGE_MAX_LENGTH}{' '}
                  characters each. Messages scroll one after another.
                </p>
              </Form.Group>
            )}
            {(field.group !== 'ticker' || tickerSource !== 'text') && (
              <Form.Group className='mb-0'>
                <Form.Label>Feed URL</Form.Label>
                <Form.Control
                  disabled={disabled}
                  value={settings.settings[field.group].feed_url || ''}
                  placeholder='https://example.com/feed.xml'
                  isInvalid={Boolean(
                    getError(errors, `settings.${field.group}.feed_url`)
                  )}
                  onChange={e =>
                    change(['settings', field.group, 'feed_url'], e.target.value)
                  }
                />
                {getError(errors, `settings.${field.group}.feed_url`) && (
                  <Form.Text className='text-danger'>
                    {getError(errors, `settings.${field.group}.feed_url`)}
                  </Form.Text>
                )}
                <p className='text-muted mb-1 mt-1'>
                  Suggested feeds:{' '}
                  {SUGGESTED_FEEDS.map((suggestion, i) => (
                    <span key={suggestion.url}>
                      <button
                        type='button'
                        disabled={disabled}
                        className='btn btn-link p-0 align-baseline shadow-none'
                        style={{ outline: 'none' }}
                        onClick={() => handlePickSuggestion(field.group, suggestion)}>
                        {suggestion.label}
                      </button>
                      {i < SUGGESTED_FEEDS.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </p>
              </Form.Group>
            )}
          </Card.Body>
        </Card>
      ))}

      {errorMessage && (
        <Alert variant='danger' className='mt-3'>
          {errorMessage}
        </Alert>
      )}

      <div className='d-flex justify-content-end' style={{ gap: '8px' }}>
        <LoaderButton
          label='Cancel'
          disabled={!hasChanges}
          variant='secondary'
          type='button'
          onClick={handleCancel}
        />
        <LoaderButton
          label='Save'
          loading={isSaving}
          disabled={!hasChanges}
          variant='success'
          type='button'
          onClick={handleSave}
        />
      </div>
    </div>
  )
}

export default ScreenWidgetSettingsSection
